/* eslint-disable */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
	RefreshCcw, Loader2, Package, Maximize2, Minimize2, AlertTriangle, Truck, Clock, Filter, Search,
	ShoppingCart, ClipboardList,
} from 'lucide-react';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import drawerActions from '@/Actions/drawer.actions';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices, laneServices } from '@/Services';
import { LANE_TYPES, LANE_TYPE_META, LaneChip } from '@/Pages/LanePlanning/laneTypes';
import {
	getHigherUnitAndQuantity, unitMap, addCommas, filtersInfoChip,
} from '@/Helpers';

const ALL_STATUSES = ['PENDING', 'DISPATCH', 'TRANSIT'];

const POLL_MS = 5 * 60 * 1000;

const hasPackedItems = (po) => (po?.items || []).some((it) => Number(it?.packaged) > 0);

// Order completion by weight. The list API sends weights in a higher unit (kg / ltr / lb /
// gal), so everything is brought back to its base unit first; a countable item weighs
// count × minWeight (POItem.minWeight, in grams). Countable items without a minWeight
// carry no weight and are left out.
const BASE_UNIT_FACTOR = {
	gram: 1, g: 1, kg: 1000, ml: 1, ltr: 1000, litre: 1000, l: 1000, oz: 1, lb: 16, floz: 1, gal: 128,
};
const COUNT_UNITS = ['count', 'pcs', 'pc', 'piece', 'pieces', 'nos'];

const itemUnitName = (item) => {
	const raw = item?.unit;
	const name = raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? unitMap[Number(raw)] : raw;
	return String(name || '').toLowerCase();
};

const weightOf = (item, qty) => {
	const unit = itemUnitName(item);
	const n = Number(qty) || 0;
	if (COUNT_UNITS.includes(unit)) return n * (Number(item?.minWeight) || 0);
	return n * (BASE_UNIT_FACTOR[unit] ?? 1);
};

// Done = packed or dispatched, whichever is further along, capped at the ordered qty so an
// excess on one item cannot make up for a shortfall on another.
const orderCompletion = (po) => {
	let total = 0;
	let done = 0;
	(po?.items || []).forEach((it) => {
		const qty = Number(it?.qty) || 0;
		const progressed = Math.min(Math.max(Number(it?.packaged) || 0, Number(it?.fulfilled) || 0), qty);
		total += weightOf(it, qty);
		done += weightOf(it, progressed);
	});
	if (total <= 0) return null;
	return { pct: (done / total) * 100, done, total };
};

const completionClass = (pct) => {
	if (pct < 50) return 'bg-red-100 text-red-700 border-red-200';
	if (pct <= 90) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
	return 'bg-green-100 text-green-700 border-green-200';
};

const fmtGrams = (g) => (g >= 1000 ? `${Number((g / 1000).toFixed(2))} kg` : `${Number(g.toFixed(0))} g`);

// jsonData.subStatus (set by the backend) only moves forward:
// PENDING -> PICKING (first scan on a child transfer order) -> PACKAGING (first scan on a
// child sub-packaging order, or packing on the order itself) -> DISPATCH -> TRANSIT.
// orderStatus stays the source of truth: subStatus only splits a PENDING order. A missing
// subStatus counts as PENDING.
const SUB_STATUS_RANK = { PENDING: 0, PICKING: 1, PACKAGING: 2 };

// Dispatch tags (tables.DISPATCH_TAG_SUB_STATUSES), set from the order's child orders once its
// boxes are made: sub-packaging closed -> transfer to storage started -> transfer closed. An order
// carrying one belongs on Pending Dispatch even while its orderStatus is still PENDING.
const DISPATCH_TAGS = {
	TRANSFER_PENDING: {
		label: 'Transfer Pending',
		className: 'bg-red-100 text-red-700 border-red-200',
		title: 'Packing done - boxes not yet moved to storage',
	},
	TRANSFER_IN_PROGRESS: {
		label: 'Transfer in Progress',
		className: 'bg-yellow-100 text-yellow-800 border-yellow-300',
		title: 'Boxes are being moved to storage',
	},
	READY_FOR_DISPATCH: {
		label: 'Ready for Dispatch',
		className: 'bg-green-100 text-green-700 border-green-200',
		title: 'All boxes are in storage - ready to dispatch',
	},
};
// Also accepts the spelled-out forms ("TRANSFER PENDING") and TRANSFER_COMPLETED as another
// name for READY_FOR_DISPATCH - values written outside tables.ORDER_SUB_STATUSES.
const DISPATCH_TAG_ALIASES = { TRANSFER_COMPLETED: 'READY_FOR_DISPATCH' };
const dispatchTagOf = (po) => {
	const key = String(po?.jsonData?.subStatus || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
	return DISPATCH_TAGS[DISPATCH_TAG_ALIASES[key] || key];
};

const pendingStageOf = (po, isInternal) => {
	if (dispatchTagOf(po)) return 'DISPATCH_TAGGED';
	const rank = SUB_STATUS_RANK[String(po?.jsonData?.subStatus || '').toUpperCase()] ?? 0;
	if (rank >= SUB_STATUS_RANK.PACKAGING) return 'PACKAGING';
	// Outlet orders have no picking step.
	if (rank === SUB_STATUS_RANK.PICKING && !isInternal) return 'PICKING';
	return 'PENDING';
};

const BOARD_COLUMNS = [
	{
		key: 'inProduction',
		title: 'Pending',
		hint: 'NOT STARTED',
		match: (po, status, isInternal) => status === 'PENDING' && pendingStageOf(po, isInternal) === 'PENDING',
		dotClass: 'bg-orange-500',
		countClass: 'bg-orange-100 text-orange-700',
		cardBorderClass: 'border-l-orange-400',
		chipClass: 'bg-orange-50 text-orange-700 border-orange-200',
		legend: 'Pending order — nothing picked or packed yet',
	},
	{
		key: 'picking',
		title: 'Picking',
		hint: 'TRANSFER IN PROGRESS',
		customerOnly: true,
		match: (po, status, isInternal) => status === 'PENDING' && pendingStageOf(po, isInternal) === 'PICKING',
		dotClass: 'bg-blue-500',
		countClass: 'bg-blue-100 text-blue-700',
		cardBorderClass: 'border-l-blue-400',
		chipClass: 'bg-blue-50 text-blue-700 border-blue-200',
		legend: 'Pending order — transfer scanning started, packaging not yet',
	},
	{
		key: 'inPackaging',
		title: 'In Packaging',
		hint: 'PACKAGING IN PROGRESS',
		match: (po, status, isInternal) => status === 'PENDING' && pendingStageOf(po, isInternal) === 'PACKAGING',
		dotClass: 'bg-green-500',
		countClass: 'bg-green-100 text-green-700',
		cardBorderClass: 'border-l-green-500',
		chipClass: 'bg-green-50 text-green-700 border-green-200',
		legend: 'Pending order — packaging scanning started',
	},
	{
		key: 'pendingDispatch',
		title: 'Pending Dispatch',
		hint: 'READY TO DISPATCH',
		match: (po, status) => status === 'DISPATCH'
			|| (status === 'PENDING' && pendingStageOf(po) === 'DISPATCH_TAGGED'),
		dotClass: 'bg-gray-800',
		countClass: 'bg-gray-800 text-white',
		cardBorderClass: 'border-l-gray-700',
		chipClass: 'bg-gray-100 text-gray-700 border-gray-200',
		legend: 'Order status is Pending Dispatch, or its boxes are made (transfer pending / in progress / ready)',
	},
];

const fmtQty = (n) => {
	const num = Number(n) || 0;
	return Number.isInteger(num) ? num : Number(num.toFixed(2));
};

const qtyDisplay = (rawUnit, rawQty) => {
	const resolved = rawUnit != null && rawUnit !== '' && !Number.isNaN(Number(rawUnit))
		? unitMap[Number(rawUnit)]
		: rawUnit;
	const [qty, unit] = getHigherUnitAndQuantity(resolved, Number(rawQty) || 0);
	return `×${fmtQty(qty)}${unit ? ` ${unit}` : ''}`;
};

const qtyLabel = (item) => qtyDisplay(item?.unit, item?.qty);

const fractionLabel = (item, doneRaw) => {
	const resolved = item?.unit != null && item?.unit !== '' && !Number.isNaN(Number(item?.unit))
		? unitMap[Number(item.unit)]
		: item?.unit;
	const [qty, unit] = getHigherUnitAndQuantity(resolved, Number(item?.qty) || 0);
	const [done] = getHigherUnitAndQuantity(resolved, Number(doneRaw) || 0);
	return `${fmtQty(done)}/${fmtQty(qty)}${unit ? ` ${unit}` : ''}`;
};

const MAX_ITEMS_ON_CARD = 5;

// Items of the child orders raised for this order, one row per variant: an item spread over
// several of those orders is summed. The backend sends two kinds (includeProcureOrders):
//   po.procureOrders      FMS_CONSUMPTION   - what is procured for it; fulfilled = moved
//   po.subPackagingOrders FMS_SUB_PACKAGING - its packing work; packaged = packed
const childItemsOf = (childOrders, prefix) => {
	const map = {};
	(childOrders || []).forEach((order) => {
		(order?.items || []).forEach((it) => {
			const key = it?.variantId ?? `${it?.productId}:${it?.id}`;
			if (!map[key]) {
				map[key] = {
					...it,
					id: `${prefix}-${key}`,
					qty: 0,
					fulfilled: 0,
					packaged: 0,
					orderRefs: [],
				};
			}
			map[key].qty += Number(it?.qty) || 0;
			map[key].fulfilled += Number(it?.fulfilled) || 0;
			map[key].packaged += Number(it?.packaged) || 0;
			map[key].orderRefs.push(order?.clientPoId || order?.id);
		});
	});
	return Object.values(map);
};

const OrderCard = ({
	po, column, shortageMap = {}, dateKey = 'scheduledAt', isInternal = false, laneName, storageLaneName,
	showProcure = false, onToggleProcure,
}) => {
	const procureItems = useMemo(() => childItemsOf(po?.procureOrders, 'procure'), [po]);
	const subPackItems = useMemo(() => childItemsOf(po?.subPackagingOrders, 'subpack'), [po]);
	const canProcure = procureItems.length > 0;
	const procureMode = canProcure && showProcure;
	const items = po?.items || [];
	// In Packaging the progress is the sub-packaging orders' packed qty. An order packed on
	// itself (no sub-packaging order) keeps its own items. Pending Dispatch: the order itself.
	const subPackMode = column.key === 'inPackaging' && subPackItems.length > 0;
	const laneItems = subPackMode ? subPackItems : items;
	const listItems = procureMode ? procureItems : laneItems;
	const [expanded, setExpanded] = useState(false);
	const visibleItems = expanded ? listItems : listItems.slice(0, MAX_ITEMS_ON_CARD);
	const hiddenCount = listItems.length - MAX_ITEMS_ON_CARD;
	const amount = po?.finalValue;
	const dispatchBy = po?.[dateKey] ?? po?.scheduledAt ?? po?.dispatchBy;

	const totalQty = laneItems.reduce((acc, it) => acc + (Number(it?.qty) || 0), 0);
	const totalPacked = laneItems.reduce((acc, it) => acc + (Number(it?.packaged) || 0), 0);
	const packedPct = totalQty > 0 ? Math.min(100, (totalPacked / totalQty) * 100) : 0;
	const itemShortage = (it) => shortageMap?.[it?.variantId];

	const shortItems = items.filter((it) => itemShortage(it));
	const hasShortage = shortItems.length > 0;
	const shortSupplyItems = column.key === 'pendingDispatch'
		? items.filter((it) => (Number(it?.fulfilled) || 0) < (Number(it?.qty) || 0))
		: [];
	const hasShortSupply = shortSupplyItems.length > 0;
	const excessSupplyItems = column.key === 'pendingDispatch'
		? items.filter((it) => (Number(it?.fulfilled) || 0) > (Number(it?.qty) || 0))
		: [];
	const hasExcessSupply = excessSupplyItems.length > 0;

	// The % follows the lane's source: Picking - the procure orders (moved), Packaging - the
	// sub-packaging orders (packed), otherwise the order itself.
	const completionSource = column.key === 'picking' && canProcure
		? 'procure'
		: subPackMode ? 'subpack' : 'order';
	const completion = orderCompletion({
		items: completionSource === 'procure' ? procureItems : laneItems,
	});
	const completionTitle = { procure: 'Picked', subpack: 'Packed', order: 'Completed' }[completionSource];

	const dispatchDatePassed = ['inProduction', 'picking'].includes(column.key)
		&& dispatchBy && moment(dispatchBy).isBefore(moment(), 'day');

	return (
		<div className={`rounded-xl border border-l-4 p-3.5 shadow-sm hover:shadow-md transition-shadow ${(hasShortage || hasShortSupply)
			? 'border-red-200 border-l-red-500 bg-red-50/40'
			: `border-gray-200 ${column.cardBorderClass} bg-white`}`}>
			{/* Line 1: completion by weight + order id, so badges can never squeeze it. */}
			<div className='flex min-w-0 items-center gap-1.5'>
				{completion && (
					<span
						className={`flex-shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-extrabold ${completionClass(completion.pct)}`}
						title={`${completionTitle} by weight: ${fmtGrams(completion.done)} of ${fmtGrams(completion.total)}`}
					>
						{Math.floor(completion.pct)}%
					</span>
				)}
				<p
					className='m-0 min-w-0 truncate text-[12px] font-extrabold text-gray-900'
					title={po?.clientPoId || String(po?.id ?? '')}
				>
					#{po?.clientPoId || po?.id}
				</p>
				{canProcure && (
					<button
						type='button'
						onClick={() => onToggleProcure?.(po?.id, procureMode)}
						title={procureMode ? 'Show order items' : 'Show items to procure'}
						className={`ml-auto inline-flex flex-shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold transition-colors ${procureMode
							? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
							: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'}`}
					>
						{procureMode ? <ClipboardList size={12} /> : <ShoppingCart size={12} />}
						{procureMode ? laneItems.length : procureItems.length}
					</button>
				)}
			</div>
			{/* Line 2: buyer / lane chips, then alert badges — wraps instead of shrinking. */}
			<div className='mt-1.5 flex flex-wrap items-center gap-1'>
				{(po?.vendorName || po?.storeName) && (
					<span
						className='max-w-full truncate rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600'
						title={isInternal ? 'Deliver to outlet' : (po?.vendorName || po?.storeName)}
					>
						{isInternal
							? (po?.storeName || po?.vendorName)
							: (po?.vendorName || po?.storeName)}
					</span>
				)}
				<LaneChip laneType={LANE_TYPES.PACKING} name={laneName} />
				<LaneChip laneType={LANE_TYPES.STORAGE} name={storageLaneName} />
				<div className='ml-auto flex flex-wrap items-center justify-end gap-1'>
					{column.key === 'pendingDispatch' && dispatchTagOf(po) && (
						<span
							className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[9px] font-bold ${dispatchTagOf(po).className}`}
							title={dispatchTagOf(po).title}
						>
							{dispatchTagOf(po).label}
						</span>
					)}
					{dispatchDatePassed && (
						<span
							className='inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-600 whitespace-nowrap'
							title={`Dispatch date passed — was due ${moment(dispatchBy).format('DD MMM, hh:mm A')}`}
						>
							<Clock size={10} />
							Dispatch Overdue
						</span>
					)}
					{hasShortage ? (
						<span
							className='inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-600 whitespace-nowrap'
							title={shortItems.map((it) => {
								const s = itemShortage(it);
								return `${it?.productName}: available ${s?.available ?? '-'}, need ${s?.demand ?? '-'}`;
							}).join(' · ')}
						>
							<AlertTriangle size={10} />
							Stock Shortage
						</span>
					) : hasShortSupply ? (
						<span
							className='inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-600 whitespace-nowrap'
							title={shortSupplyItems.map((it) =>
								`${it?.productName}: dispatched ${fractionLabel(it, it?.fulfilled)}`).join(' · ')}
						>
							<AlertTriangle size={10} />
							Short Supply
						</span>
					) : hasExcessSupply ? (
						<span
							className='inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700 whitespace-nowrap'
							title={excessSupplyItems.map((it) =>
								`${it?.productName}: dispatched ${fractionLabel(it, it?.fulfilled)}`).join(' · ')}
						>
							<AlertTriangle size={10} />
							Excess Supply
						</span>
					) : null}
				</div>
			</div>

			<div className='mt-2.5 flex flex-col gap-2'>
				{procureMode && (
					<span
						className='flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-violet-600'
						title={(po?.procureOrders || []).map((o) => o?.clientPoId || o?.id).join(', ')}
					>
						<ShoppingCart size={10} />
						To procure · {(po?.procureOrders || []).length} order{(po?.procureOrders || []).length !== 1 ? 's' : ''}
					</span>
				)}
				{!procureMode && subPackMode && (
					<span
						className='flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-green-700'
						title={(po?.subPackagingOrders || []).map((o) => o?.clientPoId || o?.id).join(', ')}
					>
						<Package size={10} />
						Sub packaging · {(po?.subPackagingOrders || []).length} order{(po?.subPackagingOrders || []).length !== 1 ? 's' : ''}
					</span>
				)}
				{visibleItems.map((item, i) => {
					// Stock shortage is worked out on the order's own items, not on what is procured.
					const shortage = procureMode ? undefined : itemShortage(item);
					const shortSupply = column.key === 'pendingDispatch'
						&& (Number(item?.fulfilled) || 0) < (Number(item?.qty) || 0);
					const excessSupply = column.key === 'pendingDispatch'
						&& (Number(item?.fulfilled) || 0) > (Number(item?.qty) || 0);
					return (
						<div key={item?.poItemId ?? item?.id ?? i} className='flex flex-col gap-0.5'>
							<div className='flex items-center justify-between gap-2'>
								<div className='flex min-w-0 items-center gap-1.5'>
									<span className='flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-amber-50 text-amber-700'>
										<Package size={10} />
									</span>
									<span
										className={`truncate text-[11px] font-medium ${(shortage || shortSupply) ? 'text-red-600' : excessSupply ? 'text-amber-600' : 'text-gray-800'}`}
										title={`${item?.productName || ''}${item?.variantName ? ` (${item.variantName})` : ''}`}
									>
										{item?.productName}
										{item?.variantName ? ` (${item.variantName})` : ''}
									</span>
								</div>
							{(() => {
								let qtyText = qtyLabel(item);
								let qtyClass = 'text-gray-500';
								let qtyTitle;
								if (procureMode) {
									const moved = Number(item?.fulfilled) || 0;
									qtyText = fractionLabel(item, moved);
									qtyClass = moved >= (Number(item?.qty) || 0) ? 'text-green-600' : 'text-orange-500';
									qtyTitle = `Procured ${qtyText} · ${(item?.orderRefs || []).join(', ')}`;
								} else if (column.key === 'inPackaging') {
									const packed = Number(item?.packaged) || 0;
									qtyText = fractionLabel(item, packed);
									const full = packed >= (Number(item?.qty) || 0);
									qtyClass = full ? 'text-green-600' : 'text-orange-500';
									qtyTitle = subPackMode
										? `Packed ${qtyText} · ${(item?.orderRefs || []).join(', ')}`
										: `Packed ${qtyText}`;
								} else if (column.key === 'pendingDispatch') {
									const disp = Number(item?.fulfilled) || 0;
									qtyText = fractionLabel(item, disp);
									const short = disp < (Number(item?.qty) || 0);
									const excess = disp > (Number(item?.qty) || 0);
									qtyClass = short
										? 'text-red-600 font-bold'
										: excess
											? 'text-amber-600 font-bold'
											: 'text-gray-600';
									qtyTitle = short
										? `Short dispatch — ${qtyText}`
										: excess
											? `Excess dispatch — ${qtyText}`
											: `Dispatched ${qtyText}`;
								}
								return (
									<span className={`whitespace-nowrap text-[10px] font-semibold ${qtyClass}`} title={qtyTitle}>
										{qtyText}
									</span>
								);
							})()}
							</div>
							{/* Line 2 of the item: its alert, indented under the name. */}
							{shortage && (
								<span
									className='flex items-center gap-1 pl-[22px] text-[9px] font-bold text-red-500'
									title={`Stock ${qtyDisplay(item?.unit, shortage.stock).replace('×', '')} − ${qtyDisplay(item?.unit, shortage.stock - shortage.available).replace('×', '')} (earlier orders + packing) = ${qtyDisplay(item?.unit, shortage.available).replace('×', '')} left, but this order needs ${qtyDisplay(item?.unit, shortage.demand).replace('×', '')}`}
								>
									<AlertTriangle size={10} className='flex-shrink-0' />
									stock: {qtyDisplay(item?.unit, shortage.stock).replace('×', '')} · available: {qtyDisplay(item?.unit, shortage.available).replace('×', '')}
								</span>
							)}
							{!shortage && shortSupply && (
								<span className='flex items-center gap-1 pl-[22px] text-[9px] font-bold text-red-500'>
									<AlertTriangle size={10} className='flex-shrink-0' />
									Short Supply
								</span>
							)}
							{!shortage && excessSupply && (
								<span className='flex items-center gap-1 pl-[22px] text-[9px] font-bold text-amber-600'>
									<AlertTriangle size={10} className='flex-shrink-0' />
									Excess Supply
								</span>
							)}
						</div>
					);
				})}
				{hiddenCount > 0 && (
					<button
						type='button'
						onClick={() => setExpanded((prev) => !prev)}
						className='self-start text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline'
					>
						{expanded
							? 'Show less'
							: `+${hiddenCount} more item${hiddenCount > 1 ? 's' : ''}`}
					</button>
				)}
				{listItems.length === 0 && (
					<span className='text-[10px] text-gray-400'>No items</span>
				)}
			</div>

			{column.key === 'inPackaging' && totalQty > 0 && (
				<div
					className='mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100'
					title={`Packed ${totalPacked} of ${totalQty}`}
				>
					<div
						className={`h-full rounded-full transition-all ${packedPct >= 100 ? 'bg-green-500' : 'bg-orange-400'}`}
						style={{ width: `${packedPct}%` }}
					/>
				</div>
			)}
			<div className='mt-2.5 flex items-center justify-between border-t border-gray-100 pt-2'>
				{/* Dispatch-by date always visible — alerts show as badges in the header. */}
				<span className='text-[10px] font-bold text-gray-700'>
					{dispatchBy ? `Dispatch by · ${moment(dispatchBy).format('DD MMM, hh:mm A')}` : ''}
				</span>
				<span className='text-[12px] font-extrabold text-gray-900'>
					{amount !== null && amount !== undefined && amount >= 0
						? `₹${addCommas(amount)}`
						: `${items.length} item${items.length !== 1 ? 's' : ''}`}
				</span>
			</div>
			{po?.id != null && (
				<p className='m-0 mt-1 text-[9px] font-medium text-gray-400'>UY ID: {po.id}</p>
			)}
		</div>
	);
};

// Item chips of one dispatched order — collapsed to 5 chips with a
// "+N more items" toggle; short/excess items are listed first so the
// problems stay visible even while collapsed.
const TransitItemChips = ({ items }) => {
	const [expanded, setExpanded] = useState(false);
	const rank = (it) => {
		const disp = Number(it?.fulfilled) || 0;
		const qty = Number(it?.qty) || 0;
		if (disp < qty) return 0;
		if (disp > qty) return 1;
		return 2;
	};
	const sorted = [...(items || [])].sort((a, b) => rank(a) - rank(b));
	const visibleItems = expanded ? sorted : sorted.slice(0, MAX_ITEMS_ON_CARD);
	const hiddenCount = sorted.length - MAX_ITEMS_ON_CARD;
	return (
		<div className='flex flex-wrap items-center gap-1.5'>
			{visibleItems.map((item, i) => {
				const disp = Number(item?.fulfilled) || 0;
				const qty = Number(item?.qty) || 0;
				const short = disp < qty;
				const excess = disp > qty;
				return (
					<span
						key={item?.poItemId ?? item?.id ?? i}
						className='inline-flex max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] text-gray-700'
						title={short
							? `Short Supply — dispatched ${fractionLabel(item, disp)}`
							: excess
								? `Excess Supply — dispatched ${fractionLabel(item, disp)}`
								: `Dispatched ${fractionLabel(item, disp)}`}
					>
						<span className='truncate font-medium'>
							{item?.productName}
							{item?.variantName ? ` (${item.variantName})` : ''}
						</span>
						<b className={`whitespace-nowrap font-extrabold ${short
							? 'text-red-600'
							: excess
								? 'text-amber-600'
								: 'text-gray-700'}`}
						>
							{fractionLabel(item, disp)}
						</b>
					</span>
				);
			})}
			{hiddenCount > 0 && (
				<button
					type='button'
					onClick={() => setExpanded((prev) => !prev)}
					className='text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline'
				>
					{expanded ? 'Show less' : `+${hiddenCount} more item${hiddenCount > 1 ? 's' : ''}`}
				</button>
			)}
			{(items || []).length === 0 && (
				<span className='text-[10px] text-gray-400'>No items</span>
			)}
		</div>
	);
};
const PAGE_ROTATE_MS = 12 * 1000;
const CARD_GAP_PX = 12; 

// measureKey: anything besides `items` that changes a card's height (e.g. the cart view).
// It re-measures the pages but keeps the page being looked at.
const ColumnCarousel = ({ items, itemKey, renderItem, measureKey }) => {
	const prevItemsRef = useRef(items);
	const viewportRef = useRef(null);
	const measureRef = useRef(null);
	const [viewportH, setViewportH] = useState(0);
	const [pages, setPages] = useState([]);
	const [page, setPage] = useState(0);

	useEffect(() => {
		const el = viewportRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;
		const ro = new ResizeObserver((entries) => {
			const h = Math.floor(entries?.[0]?.contentRect?.height || 0);
			setViewportH((prev) => (prev === h ? prev : h));
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useLayoutEffect(() => {
		if (!items.length) {
			setPages([]);
			setPage(0);
			return;
		}
		const nodes = Array.from(measureRef.current?.children || []);
		if (!viewportH || nodes.length !== items.length) {
			setPages([items.map((_, i) => i)]);
			setPage(0);
			return;
		}
		const built = [];
		let cur = [];
		let used = 0;
		nodes.forEach((node, i) => {
			const h = node.offsetHeight;
			const needed = cur.length === 0 ? h : used + CARD_GAP_PX + h;
			if (cur.length > 0 && needed > viewportH) {
				built.push(cur);
				cur = [i];
				used = h;
			} else {
				cur.push(i);
				used = needed;
			}
		});
		if (cur.length) built.push(cur);
		setPages(built);
		const itemsChanged = prevItemsRef.current !== items;
		prevItemsRef.current = items;
		setPage((prev) => (itemsChanged || prev >= built.length ? 0 : prev));
	}, [items, viewportH, measureKey]);

	// Auto-rotate through the pages.
	useEffect(() => {
		if (pages.length <= 1) return undefined;
		const timer = setInterval(() => {
			setPage((prev) => (prev + 1) % pages.length);
		}, PAGE_ROTATE_MS);
		return () => clearInterval(timer);
	}, [pages.length]);

	const safePage = page < pages.length ? page : 0;
	const visible = pages[safePage] || [];

	return (
		<div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
			<div ref={viewportRef} className='min-h-0 flex-1 overflow-hidden'>
				<div className='flex flex-col gap-3'>
					{visible.map((idx) => (
						<div key={itemKey(items[idx]) ?? idx}>{renderItem(items[idx], idx)}</div>
					))}
				</div>
			</div>
			{/* invisible copy of every card at the real column width — measurement only */}
			<div
				ref={measureRef}
				aria-hidden
				className='pointer-events-none invisible absolute inset-x-0 top-0 -z-10 flex flex-col gap-3'
			>
				{items.map((it, i) => (
					<div key={itemKey(it) ?? i}>{renderItem(it, i)}</div>
				))}
			</div>
			{pages.length > 1 && (
				<div className='mt-2 flex flex-shrink-0 items-center justify-center gap-1.5'>
					{pages.map((_, i) => (
						<button
							key={i}
							type='button'
							onClick={() => setPage(i)}
							title={`Page ${i + 1}`}
							className={`h-1.5 rounded-full transition-all ${i === safePage
								? 'w-5 bg-gray-700'
								: 'w-1.5 bg-gray-300 hover:bg-gray-400'}`}
						/>
					))}
					<span className='ml-1 text-[9px] font-bold text-gray-400'>
						{safePage + 1}/{pages.length}
					</span>
				</div>
			)}
		</div>
	);
};

const BuyerOrdersBoard = () => {
	/* ------------------------------ state ------------------------------ */
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(false);
	// Auto-refresh: when the next silent fetch is due. Every fetch (manual, filter, auto) pushes it out.
	const [nextRefreshAt, setNextRefreshAt] = useState(() => Date.now() + POLL_MS);
	const [secondsLeft, setSecondsLeft] = useState(POLL_MS / 1000);
	const [stockMap, setStockMap] = useState({});
	const [orderSource, setOrderSource] = useState('external');
	// Default window: today + next two days (e.g. 25, 26, 27).
	const [dateRange, setDateRange] = useState([
		moment().toDate(),
		moment().add(2, 'day').toDate(),
	]);
	const [storeFilter, setStoreFilter] = useState(null);
	const [appliedFilters, setAppliedFilters] = useState({});
	const [activeTab, setActiveTab] = useState('board');
	const [viewMode, setViewMode] = useState('orders');
	const [search, setSearch] = useState('');
	// Lane Planning data. Packing lanes: the lane filter and the packing chip on cards.
	// Storage lanes: the storage chip only.
	const [lanes, setLanes] = useState([]);
	const [laneAssignments, setLaneAssignments] = useState({});
	const [storageLanes, setStorageLanes] = useState([]);
	const [storageLaneAssignments, setStorageLaneAssignments] = useState({});
	const [laneFilter, setLaneFilter] = useState('ALL');
	const [isFullscreen, setIsFullscreen] = useState(false);
	// Order id -> true/false once the user toggled a card between the items to procure and the
	// order's own items. Untoggled cards follow the lane: Picking opens on the cart view.
	const [procureViewIds, setProcureViewIds] = useState({});
	const toggleProcureView = useCallback((poId, current) => {
		setProcureViewIds((prev) => ({ ...prev, [poId]: !current }));
	}, []);
	const showProcureOf = (poId, colKey) => procureViewIds[poId] ?? colKey === 'picking';
	const dateTouchedRef = useRef(false);
	const boardRef = useRef(null);
	const reduxDispatch = useDispatch();

	/* --------------------- functions & derived data --------------------- */
	const fetchStock = async (poList) => {
		try {
			const variantIds = [...new Set((poList || [])
				.flatMap((po) => (po?.items || []).map((it) => it?.variantId))
				.filter((v) => v !== null && v !== undefined))];
			const storeIds = [...new Set((poList || [])
				.map((po) => po?.fromStore)
				.filter((v) => v !== null && v !== undefined))];
			if (!variantIds.length || !storeIds.length) {
				setStockMap({});
				return;
			}
			const res = await inventoryAnalyticsServices.fetchStockReport({
				req: {
					data: {
						storeIds,
						variantIds,
					},
				},
			});
			if (res?.statusCode === 200) {
				// Response shape: { [storeId]: [ { variantId, currentStock, ... } ] }
				// -> variantId -> currentStock summed across the queried stores.
				const map = {};
				Object.values(res?.data || {}).forEach((rows) => {
					(Array.isArray(rows) ? rows : []).forEach((row) => {
						const vid = row?.variantId;
						if (vid === null || vid === undefined) return;
						const qty = Number(row?.currentStock);
						map[vid] = (map[vid] || 0) + (Number.isFinite(qty) ? qty : 0);
					});
				});
				setStockMap(map);
			}
		} catch (error) {
			console.error('Failed to fetch stock report', error);
		}
	};

	const availabilityMap = useMemo(() => {
		const demand = {};
		const reserved = {};
		(orders || []).forEach((po) => {
			const status = String(po?.orderStatus || '').toUpperCase();
			if (status === 'PENDING') {
				const packingStarted = hasPackedItems(po);
				(po?.items || []).forEach((it) => {
					const vid = it?.variantId;
					if (vid === null || vid === undefined) return;
					if (packingStarted) {
						reserved[vid] = (reserved[vid] || 0) + (Number(it?.packaged) || 0);
					} else {
						demand[vid] = (demand[vid] || 0) + (Number(it?.qty) || 0);
					}
				});
			} else if (status === 'DISPATCH') {
				(po?.items || []).forEach((it) => {
					const vid = it?.variantId;
					if (vid === null || vid === undefined) return;
					reserved[vid] = (reserved[vid] || 0) + (Number(it?.qty) || 0);
				});
			}
		});
		const map = {};
		Object.keys(stockMap).forEach((vid) => {
			const stock = stockMap[vid];
			map[vid] = {
				stock,
				reserved: reserved[vid] || 0,
				available: stock - (reserved[vid] || 0),
				demand: demand[vid] || 0,
			};
		});
		return map;
	}, [orders, stockMap]);

	const aggShortageMap = useMemo(() => {
		const map = {};
		Object.entries(availabilityMap).forEach(([vid, info]) => {
			if (info.demand > info.available) map[vid] = info;
		});
		return map;
	}, [availabilityMap]);

	const loadLaneData = useCallback(async () => {
		const sortLanes = (list) => (list || []).slice().sort((a, b) => String(a?.name || '')
			.localeCompare(String(b?.name || ''), undefined, { numeric: true, sensitivity: 'base' }));
		const toMap = (list) => {
			const map = {};
			(list || []).forEach((a) => {
				if (a?.poId != null && a?.laneId != null) map[a.poId] = a.laneId;
			});
			return map;
		};
		try {
			const [lanesRes, assignRes, storageLanesRes, storageAssignRes] = await Promise.all([
				laneServices.fetchLanes(LANE_TYPES.PACKING),
				laneServices.fetchLaneAssignments(LANE_TYPES.PACKING),
				laneServices.fetchLanes(LANE_TYPES.STORAGE),
				laneServices.fetchLaneAssignments(LANE_TYPES.STORAGE),
			]);
			if (lanesRes?.statusCode === 200) setLanes(sortLanes(lanesRes?.data));
			if (assignRes?.statusCode === 200) setLaneAssignments(toMap(assignRes?.data));
			if (storageLanesRes?.statusCode === 200) setStorageLanes(sortLanes(storageLanesRes?.data));
			if (storageAssignRes?.statusCode === 200) setStorageLaneAssignments(toMap(storageAssignRes?.data));
		} catch (error) {
			// laneServices already surfaces a toast via handleResponse
		}
	}, []);

	const fetchOrders = useCallback(async (range, source, store, silent = false) => {
		loadLaneData();
		const effective = range || dateRange;
		const effectiveSource = source || orderSource;
		const effectiveStore = store === undefined ? storeFilter : store;
		setNextRefreshAt(Date.now() + POLL_MS);
		if (!silent) setLoading(true);
		try {
			const res = await inventoryAnalyticsServices.fetchPoList({
				req: {
					data: {
						filters: {
							fromStoreId: effectiveStore ?? null,
							status: ALL_STATUSES,
							buyers: [],
							variantIds: [],
							orderType: effectiveSource === 'internal' ? 'sales/purchase' : 'buyers',
							userOrderType: 'sales',
							dateRangeFilter: {
								dateType: '',
								from: moment(effective?.[0]).format('YYYY-MM-DD'),
								to: moment(effective?.[1]).format('YYYY-MM-DD'),
							},
						},
						showInternalData: false,
						// Adds po.procureOrders: the FMS_CONSUMPTION orders raised for each order.
						includeProcureOrders: true,
					},
				},
			});
			if (res?.statusCode === 200) {
				setOrders(res?.data || []);
				fetchStock(res?.data || []);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to load buyer orders.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: typeof error === 'string' ? error : 'Failed to load buyer orders.',
			});
		} finally {
			if (!silent) setLoading(false);
		}
	}, [dateRange, orderSource, storeFilter, loadLaneData]);

	const laneNamesOf = (laneList, assignmentMap) => {
		const nameById = {};
		laneList.forEach((l) => { nameById[l.id] = l.name; });
		const map = {};
		Object.keys(assignmentMap).forEach((poId) => {
			const name = nameById[assignmentMap[poId]];
			if (name) map[poId] = name;
		});
		return map;
	};
	const laneNameByPoId = useMemo(() => laneNamesOf(lanes, laneAssignments), [lanes, laneAssignments]);
	const storageLaneNameByPoId = useMemo(
		() => laneNamesOf(storageLanes, storageLaneAssignments),
		[storageLanes, storageLaneAssignments]
	);

	// Lanes are only assigned to customer (buyer) orders, so the filter is
	// meaningless on Outlets Orders; it is also hidden while no lanes exist. A
	// selected lane that has since been removed falls back to All.
	const showLaneFilter = orderSource === 'external' && lanes.length > 0;
	const activeLaneFilter = showLaneFilter && lanes.some((l) => String(l.id) === laneFilter)
		? laneFilter
		: 'ALL';

	// ImagesFinder's props are kept referentially stable: the board re-renders every second
	// for the refresh countdown, and fresh props each tick re-rendered the date picker with a
	// new value, which reset any range being picked back to the applied one.
	const filtersInfo = useMemo(() => ({
		header: {
			date: false,
			statusAndTypes: false,
			store: false,
		},
		store: {
			text: 'Store',
			show: true,
		},
		dateRange: {
			show: true,
			text: 'Order Date',
		},
	}), []);
	const finderInitialState = useMemo(() => ({
		dateRange: {
			active: true,
			from: moment().toDate(),
			to: moment().add(2, 'day').toDate(),
		},
	}), []);
	const finderExportInfo = useMemo(() => ({ hideExportAccordian: true }), []);

	const handleFinderSubmit = (req) => {
		const from = req?.dateRange?.from ? moment(req.dateRange.from).toDate() : dateRange[0];
		const to = req?.dateRange?.to ? moment(req.dateRange.to).toDate() : dateRange[1];
		const storeId = req?.store?.id ?? null;
		const isDefaultRange = moment(from).isSame(moment(), 'day')
			&& moment(to).isSame(moment().add(2, 'day'), 'day');
		dateTouchedRef.current = !isDefaultRange;
		const hasPastDate = moment(from).isBefore(moment(), 'day');
		if (hasPastDate) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Orders are fetched as per the selected date range. Select the current date to see live orders.',
			});
		}
		setAppliedFilters(req);
		setDateRange([from, to]);
		setStoreFilter(storeId);
		fetchOrders([from, to], undefined, storeId);
	};
	const handleFinderSubmitRef = useRef(handleFinderSubmit);
	handleFinderSubmitRef.current = handleFinderSubmit;
	const onFinderSubmit = useCallback((req) => handleFinderSubmitRef.current(req), []);

	const handleSourceChange = (source) => {
		if (source === orderSource) return;
		setOrderSource(source);
		setOrders([]);
		setStockMap({});
		fetchOrders(undefined, source);
	};

	const transitOrders = useMemo(() => (orders || []).filter(
		(po) => String(po?.orderStatus || '').toUpperCase() === 'TRANSIT'
	), [orders]);

	const dispatchDateOf = (po) => {
		const primary = orderSource === 'internal' ? po?.dispatchBy : po?.scheduledAt;
		return primary ?? po?.scheduledAt ?? po?.dispatchBy ?? po?.createdAt;
	};

	const dispatchTsOf = (po) => {
		const m = moment(dispatchDateOf(po));
		return m.isValid() ? m.valueOf() : Number.MAX_SAFE_INTEGER;
	};
	const orderShortageMap = useMemo(() => {
		const remaining = {};
		Object.entries(availabilityMap).forEach(([vid, info]) => {
			remaining[vid] = Math.max(info.available, 0);
		});
		const map = {};
		const pendingOrders = (orders || [])
			.filter((po) => String(po?.orderStatus || '').toUpperCase() === 'PENDING')
			.sort((a, b) => dispatchTsOf(a) - dispatchTsOf(b));
		pendingOrders.forEach((po) => {
			(po?.items || []).forEach((it) => {
				const vid = it?.variantId;
				if (vid === null || vid === undefined || remaining[vid] === undefined) return;
				const qty = Number(it?.qty) || 0;
				const packaged = Number(it?.packaged) || 0;
				const need = Math.max(qty - packaged, 0);
				if (need <= 0) return;
				if (need <= remaining[vid]) {
					remaining[vid] -= need;
				} else {
					if (!map[po?.id]) map[po.id] = {};
					map[po.id][vid] = {
						...availabilityMap[vid],
						available: Math.max(remaining[vid], 0),
						demand: need,
					};
					remaining[vid] = 0;
				}
			});
		});
		return map;
	}, [orders, availabilityMap, orderSource]);

	const columns = useMemo(() => {
		const term = search.trim().toLowerCase();
		const matchesSearch = (po) => !term
			|| String(po?.clientPoId || po?.id || '').toLowerCase().includes(term)
			|| String(po?.vendorName || po?.storeName || '').toLowerCase().includes(term)
			|| (po?.items || []).some((it) => String(it?.productName || '').toLowerCase().includes(term));
		const matchesLane = (po) => activeLaneFilter === 'ALL'
			|| String(laneAssignments[po?.id]) === activeLaneFilter;
		const isInternal = orderSource === 'internal';
		return BOARD_COLUMNS.filter((col) => !(col.customerOnly && isInternal)).map((col) => ({
			...col,
			orders: (orders || [])
				.filter((po) => col.match(po, String(po?.orderStatus || '').toUpperCase(), isInternal))
				.filter(matchesSearch)
				.filter(matchesLane)
				.sort((a, b) => dispatchTsOf(a) - dispatchTsOf(b)),
		}));
	}, [orders, search, orderSource, activeLaneFilter, laneAssignments]);

	const boardOrderCount = useMemo(
		() => columns.reduce((acc, col) => acc + col.orders.length, 0),
		[columns]
	);

	const itemsOfColumn = (colOrders) => {
		const map = {};
		(colOrders || []).forEach((po) => {
			(po?.items || []).forEach((it) => {
				const vid = it?.variantId;
				if (vid === null || vid === undefined) return;
				if (!map[vid]) {
					map[vid] = {
						variantId: vid,
						productName: it?.productName,
						variantName: it?.variantName,
						unit: it?.unit,
						totalQty: 0,
						orderCount: 0,
					};
				}
				map[vid].totalQty += Number(it?.qty) || 0;
				map[vid].orderCount += 1;
			});
		});
		const term = search.trim().toLowerCase();
		return Object.values(map)
			.filter((it) => !term || String(it?.productName || '').toLowerCase().includes(term))
			.sort((a, b) =>
				String(a?.productName || '').localeCompare(String(b?.productName || '')));
	};

	const toggleFullscreen = () => {
		if (document.fullscreenElement) {
			document.exitFullscreen?.();
		} else {
			boardRef.current?.requestFullscreen?.();
		}
	};

	/* ------------------------------ effects ------------------------------ */
	// Initial load happens via ImagesFinder's own mount call (it submits the
	// seeded 3-day date range through handleFinderSubmit) — no effect needed.

	// Kept in a ref so the countdown below doesn't restart each time fetchOrders is re-created.
	const fetchOrdersRef = useRef(fetchOrders);
	fetchOrdersRef.current = fetchOrders;

	useEffect(() => {
		const tick = () => {
			const left = Math.max(0, Math.ceil((nextRefreshAt - Date.now()) / 1000));
			setSecondsLeft(left);
			if (left === 0) {
				const range = dateTouchedRef.current
					? undefined
					: [moment().toDate(), moment().add(2, 'day').toDate()];
				fetchOrdersRef.current(range, undefined, undefined, true);
			}
		};
		tick();
		const timer = setInterval(tick, 1000);
		return () => clearInterval(timer);
	}, [nextRefreshAt]);

	useEffect(() => {
		const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
		document.addEventListener('fullscreenchange', onFsChange);
		return () => document.removeEventListener('fullscreenchange', onFsChange);
	}, []);

	return (
		<div
			ref={boardRef}
			className={`flex w-full flex-col gap-3 bg-white p-4 text-left ${isFullscreen ? 'h-screen overflow-hidden' : 'h-[calc(100vh-80px)]'}`}
		>
			<ImagesFinder
				onSubmit={onFinderSubmit}
				filtersInfo={filtersInfo}
				initialState={finderInitialState}
				hideDateRangeTimeType
				exportInfo={finderExportInfo}
			/>

			{/* header */}
			<div className='flex flex-wrap items-center gap-3'>
				<p className='m-0 text-[20px] font-bold text-gray-900'>Orders Board</p>
				<div className='ml-2 flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1'>
					{[
						{ key: 'external', label: 'Customer Orders' },
						{ key: 'internal', label: 'Outlets Orders' },
					].map((src) => (
						<button
							key={src.key}
							type='button'
							onClick={() => handleSourceChange(src.key)}
							className={`rounded-md px-4 py-1.5 text-[13px] transition-all ${orderSource === src.key
								? 'bg-white font-bold text-gray-900 shadow-sm'
								: 'font-semibold text-gray-500 hover:text-gray-700'}`}
						>
							{src.label}
						</button>
					))}
				</div>
				<div className='ml-auto flex items-center gap-2'>
					<div className='[&>div]:!m-0 [&_*]:!text-[11px]'>
						{filtersInfoChip(filtersInfo, appliedFilters)}
					</div>
					<button
						type='button'
						onClick={() => {
							// The drawer portals to document.body, which stays hidden
							// while the board element is fullscreen — exit first.
							if (document.fullscreenElement) document.exitFullscreen?.();
							reduxDispatch(drawerActions.toggleFiltersDrawer(true));
						}}
						className='flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-teal-700'
					>
						Filter
						<Filter size={13} />
					</button>
					<button
						type='button'
						onClick={() => fetchOrders()}
						title={`Auto-refreshes in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')} - click to refresh now`}
						className='flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-gray-700 hover:bg-gray-50'
					>
						<RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
						<span className='text-[12px] font-medium tabular-nums'>
							{Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
						</span>
					</button>
					<button
						type='button'
						onClick={toggleFullscreen}
						title={isFullscreen ? 'Exit full screen' : 'Full screen'}
						className='flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-700 hover:bg-gray-50'
					>
						{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
					</button>
				</div>
			</div>

			<div className='flex items-center gap-6 border-b border-gray-200'>
				{[
					{ key: 'board', label: 'Scheduled Orders', count: boardOrderCount },
					{ key: 'dispatched', label: 'Dispatched Orders', count: transitOrders.length },
				].map((tab) => (
					<button
						key={tab.key}
						type='button'
						onClick={() => setActiveTab(tab.key)}
						className={`flex items-center gap-1.5 border-b-2 pb-1.5 text-[13px] transition-colors ${activeTab === tab.key
							? 'border-gray-900 font-bold text-gray-900'
							: 'border-transparent font-semibold text-gray-400 hover:text-gray-600'}`}
					>
						{tab.label}
						<span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${activeTab === tab.key
							? 'bg-gray-200 text-gray-800'
							: 'bg-gray-100 text-gray-400'}`}
						>
							{tab.count}
						</span>
					</button>
				))}
				{activeTab === 'board' && (
					<div className='ml-auto flex items-center gap-4'>
						{showLaneFilter && (() => {
							const { Icon } = LANE_TYPE_META[LANE_TYPES.PACKING];
							return (
							<div className='relative mb-1'>
							<Icon size={13} className='pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-teal-700' />
							<select
								value={activeLaneFilter}
								onChange={(e) => setLaneFilter(e.target.value)}
								title='Filter by packing lane'
								className='w-[170px] rounded-md border border-gray-200 bg-white py-1 pl-7 pr-2 text-[12px] focus:border-amber-400 focus:outline-none'
							>
								<option value='ALL'>All Packing Lanes</option>
								{lanes.map((l) => (
									<option key={l.id} value={String(l.id)}>{l.name}</option>
								))}
							</select>
							</div>
							);
						})()}
						<div className='relative mb-1'>
							<Search size={13} className='absolute left-2 top-1/2 -translate-y-1/2 text-gray-400' />
							<input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder='Search order / buyer / product…'
								className='w-[240px] rounded-md border border-gray-200 py-1 pl-7 pr-2 text-[12px] focus:border-amber-400 focus:outline-none'
							/>
						</div>
						{[
							{ key: 'orders', label: 'By Orders' },
							{ key: 'items', label: 'By Items' },
						].map((mode) => (
							<button
								key={mode.key}
								type='button'
								onClick={() => setViewMode(mode.key)}
								className={`border-b-2 pb-1.5 text-[12px] transition-colors ${viewMode === mode.key
									? 'border-gray-900 font-bold text-gray-900'
									: 'border-transparent font-semibold text-gray-400 hover:text-gray-600'}`}
							>
								{mode.label}
							</button>
						))}
					</div>
				)}
			</div>

			{activeTab === 'board' && (
				<>
					<div className={`grid min-h-0 flex-1 grid-cols-1 gap-4 ${columns.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
						{columns.map((col) => {
							const colData = viewMode === 'orders' ? col.orders : itemsOfColumn(col.orders);
							return (
							<div key={col.key} className='flex min-h-0 flex-col rounded-xl bg-gray-50/70 border border-gray-100'>
								<div className='flex items-center gap-2 px-3 py-2.5'>
									<span className={`h-2 w-2 rounded-full ${col.dotClass}`} />
									<span className='text-[12px] font-bold text-gray-800'>{col.title}</span>
									<span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${col.countClass}`}>
										{colData.length}
									</span>
									<span className='ml-auto text-[9px] font-bold tracking-wider text-gray-400'>
										{col.hint}
									</span>
								</div>
								<div className='flex min-h-0 flex-1 flex-col px-3 pb-3'>
									{colData.length > 0 ? (
										<ColumnCarousel
											items={colData}
											itemKey={(entry) => (viewMode === 'orders' ? entry?.id : entry?.variantId)}
											measureKey={viewMode === 'orders'
												? colData.filter((po) => showProcureOf(po?.id, col.key)).map((po) => po?.id).join(',')
												: ''}
											renderItem={(entry) => {
												if (viewMode === 'orders') {
													return (
														<OrderCard
															po={entry}
															column={col}
															shortageMap={['inProduction', 'picking', 'inPackaging'].includes(col.key) ? orderShortageMap?.[entry?.id] : undefined}
															dateKey={orderSource === 'internal' ? 'dispatchBy' : 'scheduledAt'}
															isInternal={orderSource === 'internal'}
															laneName={laneNameByPoId[entry?.id]}
															storageLaneName={storageLaneNameByPoId[entry?.id]}
															showProcure={showProcureOf(entry?.id, col.key)}
															onToggleProcure={toggleProcureView}
														/>
													);
												}
												const shortage = ['inProduction', 'picking', 'inPackaging'].includes(col.key)
													? aggShortageMap?.[entry?.variantId]
													: undefined;
												return (
													<div
														className={`flex items-center justify-between gap-2 rounded-lg border border-l-4 ${shortage
															? 'border-red-200 border-l-red-500 bg-red-50/40'
															: `border-gray-200 ${col.cardBorderClass} bg-white`} px-3 py-2 shadow-sm`}
													>
														<div className='flex min-w-0 items-center gap-1.5'>
															<span className='flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-amber-50 text-amber-700'>
																<Package size={10} />
															</span>
															<div className='min-w-0'>
																<p
																	className={`m-0 truncate text-[11px] font-semibold ${shortage ? 'text-red-600' : 'text-gray-800'}`}
																	title={shortage
																		? `Stock Shortage — available ${shortage.available} (stock ${shortage.stock} − packing/dispatch ${shortage.reserved}), needed ${shortage.demand}`
																		: undefined}
																>
																	{entry?.productName}
																	{entry?.variantName ? ` (${entry.variantName})` : ''}
																</p>
																<p className='m-0 text-[10px] text-gray-400'>
																	in {entry?.orderCount} order{entry?.orderCount !== 1 ? 's' : ''}
																</p>
															</div>
															{shortage && (
																<AlertTriangle size={11} className='flex-shrink-0 text-red-500' />
															)}
														</div>
														<span className='whitespace-nowrap text-[11px] font-extrabold text-gray-800'>
															{qtyDisplay(entry?.unit, entry?.totalQty)}
														</span>
													</div>
												);
											}}
										/>
									) : (
										!loading && (
											<div className='flex flex-1 items-center justify-center py-8 text-[12px] text-gray-400'>
												No orders
											</div>
										)
									)}
								</div>
							</div>
							);
						})}
					</div>
				</>
			)}

			{activeTab === 'dispatched' && (
				<div className='flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto'>
					{transitOrders.length > 0 ? (
						transitOrders.map((po) => {
							const poItems = po?.items || [];
							const shortCount = poItems.filter((it) => (Number(it?.fulfilled) || 0) < (Number(it?.qty) || 0)).length;
							const excessCount = poItems.filter((it) => (Number(it?.fulfilled) || 0) > (Number(it?.qty) || 0)).length;
							const who = orderSource === 'internal'
								? (po?.storeName || po?.vendorName)
								: (po?.vendorName || po?.storeName);
							const dispatchedOn = po?.dispatchedAt ?? po?.updatedAt ?? po?.createdAt;
							const amount = po?.finalValue;
							return (
								<div
									key={po?.id}
									className='flex flex-col gap-2 rounded-lg border border-gray-200 border-l-4 border-l-indigo-400 bg-white px-3 py-2.5 shadow-sm'
								>
									<div className='flex flex-wrap items-center gap-2'>
										<span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-indigo-100 text-indigo-600'>
											<Truck size={12} />
										</span>
										<span className='text-[13px] font-extrabold text-gray-900'>
											#{po?.clientPoId || po?.id}
										</span>
										{who && (
											<span
												className='rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600'
												title={orderSource === 'internal' ? 'Deliver to outlet' : undefined}
											>
												{who}
											</span>
										)}
										<LaneChip laneType={LANE_TYPES.PACKING} name={laneNameByPoId[po?.id]} />
										<LaneChip laneType={LANE_TYPES.STORAGE} name={storageLaneNameByPoId[po?.id]} />
										{shortCount > 0 && (
											<span className='inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-600 whitespace-nowrap'>
												<AlertTriangle size={10} />
												Short Supply · {shortCount} item{shortCount > 1 ? 's' : ''}
											</span>
										)}
										{excessCount > 0 && (
											<span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700 whitespace-nowrap'>
												<AlertTriangle size={10} />
												Excess Supply · {excessCount} item{excessCount > 1 ? 's' : ''}
											</span>
										)}
										<span className='ml-auto inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold text-violet-700 whitespace-nowrap'>
											<Clock size={10} />
											{dispatchedOn ? moment(dispatchedOn).format('DD MMM, hh:mm A') : '-'}
										</span>
										<span className='text-[13px] font-extrabold text-gray-900'>
											{amount !== null && amount !== undefined && amount >= 0
												? `₹${addCommas(amount)}`
												: `${poItems.length} item${poItems.length !== 1 ? 's' : ''}`}
										</span>
									</div>
									<TransitItemChips items={poItems} />
								</div>
							);
						})
					) : (
						!loading && (
							<div className='flex items-center justify-center py-16 text-[12px] text-gray-400'>
								No orders in transit for the selected date range
							</div>
						)
					)}
				</div>
			)}

			{loading && (
				<div className='fixed inset-0 z-[1200] flex items-center justify-center bg-white/40 backdrop-blur-[1px]'>
					<Loader2 size={30} className='animate-spin text-amber-500' />
				</div>
			)}
		</div>
	);
};

export default BuyerOrdersBoard;
